import { html, raw, escape } from '../core/html.js';
import { t } from '../core/i18n.js';

/**
 * Inline markup for article copy: `code`, **strong** and [label](https://…).
 * The text is escaped before any markup is applied, so a translation can never
 * inject an element; links must point to an absolute https URL.
 */
export const inline = (text) =>
  raw(
    String(text)
      .split(/(`[^`]+`)/)
      .map((part) => {
        if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) {
          return `<code>${escape(part.slice(1, -1))}</code>`;
        }
        return escape(part)
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, href) => {
            if (!href.startsWith('https://')) throw new Error(`Article links must be https: ${href}`);
            return `<a class="link" href="${href}">${label}</a>`;
          });
      })
      .join(''),
  );

const sample = (block) => html`
  <figure class="post-sample">
    <figcaption class="post-sample__caption">${inline(block.caption)}</figcaption>
    <blockquote class="post-sample__text" lang="${block.lang}">
      <p>
        <span class="post-sample__prompt">
          <span class="visually-hidden">${t('blog.sample.prompt')}: </span>${block.prompt}
        </span>
        <span class="post-sample__output">
          <span class="visually-hidden">${t('blog.sample.output')}: </span>${block.output}
        </span>
      </p>
    </blockquote>
  </figure>
`;

const stats = (block) => html`
  <dl class="post-stats">
    ${block.items.map(
      (item) => html`
        <div class="post-stats__item">
          <dt class="post-stats__label">${item.label}</dt>
          <dd class="post-stats__value">${item.value}</dd>
        </div>
      `,
    )}
  </dl>
`;

/**
 * Render the article body. Each block is `{ type, … }`; `figure` blocks are
 * delegated to `renderFigure(id, number)`, which numbers them in order.
 */
export const articleBody = (blocks, renderFigure) => {
  let figureNumber = 0;
  return blocks.map((block) => {
    switch (block.type) {
      case 'p':
        return html`<p>${inline(block.text)}</p>`;
      case 'h2':
        return html`<h2 id="${block.id}">${block.text}</h2>`;
      case 'list':
        return html`<ul>${block.items.map((item) => html`<li>${inline(item)}</li>`)}</ul>`;
      case 'note':
        return html`
          <aside class="post-note">
            <p class="post-note__title">${block.title}</p>
            <p>${inline(block.text)}</p>
          </aside>
        `;
      case 'stats':
        return stats(block);
      case 'sample':
        return sample(block);
      case 'figure':
        figureNumber += 1;
        return renderFigure(block.id, figureNumber);
      default:
        throw new Error(`Unknown article block type: ${block.type}`);
    }
  });
};

/** The h2 blocks, in order, for the table of contents. */
export const articleHeadings = (blocks) =>
  blocks.filter((block) => block.type === 'h2').map((block) => ({ id: block.id, text: block.text }));
